import { useCallback, useRef, useEffect } from 'react';
import { useWatch, useFormContext } from 'react-hook-form';
import toast from 'react-hot-toast';

export default function useDebounce<T extends (...args: any[]) => any>(callback: T, delay: number): T {
  // React 19's useRef requires an explicit initial value.
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const debouncedCallback = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay],
  ) as T;

  return debouncedCallback;
}

export function useDebouncedSave(
  fieldName: string,
  saveFn?: (value: string) => Promise<void>,
  options: {
    delay?: number;
    setIsSaving?: (isSaving: boolean) => void;
    enabled?: boolean;
  } = {},
) {
  const { delay = 2000, setIsSaving, enabled = true } = options;
  const { control, trigger } = useFormContext();

  // Watch field value
  const fieldValue = useWatch({
    control,
    name: fieldName,
  });

  // Track previous value to avoid unnecessary saves
  const previousValueRef = useRef<string | undefined>(undefined);
  const isInitializedRef = useRef(false);

  // Debounced save function
  const debouncedSave = useDebounce(async (value: string) => {
    if (enabled && value !== undefined) {
      if (setIsSaving) {
        setIsSaving(true);
      }
      try {
        const isValid = await trigger(fieldName);
        if (isValid && saveFn) {
          await saveFn(value);
        }
      } catch (e) {
        console.error(e);
        toast.error('Failed to save field');
      } finally {
        if (setIsSaving) {
          setIsSaving(false);
        }
      }
    }
  }, delay);

  // Trigger debounced save when field value changes
  useEffect(() => {
    if (enabled && fieldValue !== undefined) {
      // Only trigger save if value has actually changed and we're not in the initial load
      const hasChanged = fieldValue !== previousValueRef.current;

      if (hasChanged && isInitializedRef.current) {
        debouncedSave(fieldValue);
      }

      // Update the previous value and mark as initialized
      previousValueRef.current = fieldValue;
      isInitializedRef.current = true;
    }
  }, [fieldValue, debouncedSave, enabled]);

  return {
    fieldValue,
  };
}
